import React from "react";
import { PropertyDetailCertService, TabuService, TWIMC } from "@my-dld/utils/TabuServices";
import { LocaleUtil } from "@eres/core";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import { getParticipantID } from "@10x/sso";
import { TabuProcedure, TWICRequest } from "@10x/tabu-mobile-api/dist/requests/entity";
import { saveTWIMC, saveTWIMCForProperty } from "@10x/tabu-mobile-api/dist/requests/api";
import { TabuRequestUtils, TabuUtils } from "@10x/tabu-mobile-api";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import BaseTabuRequestForm, { addAgentParticipantId, BaseTabuRequestProps } from "./BaseTabuRequestForm";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import PageContent from "@my-dld/components/PageContent";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { Redirect } from "react-router";
import { extractPropInfo } from "../MyDLD/PropertyDetails";
import { TabuRequestContext } from "@my-dld/contexts/TabuRequestContext";
import { ConnectedPortfolioProps, portfolioConnector, } from "@my-dld/contexts/PortfolioContext";
import { LocaleValue } from "@eres/core/dist/LocaleValue";
import { SpinnerLoader } from "@my-dld/components/SpinnerLoader";

interface Props extends BaseTabuRequestProps, ConnectedPortfolioProps {
	isPropertyLevel?: boolean;
}

interface Option {
	identity: number | string;
	label: LocaleValue;
}

interface State {
	service: TabuService;
	ready: boolean;
	options?: Option[];
	requestForId?: string;
}

class TWIMCForm extends BaseTabuRequestForm<Props, State> {
	static contextType = TabuRequestContext;
	context!: React.ContextType<typeof TabuRequestContext>;
	loader: any;

	constructor( props: Props ) {
		super( props );
		this.state = {
			service: props.isPropertyLevel ? PropertyDetailCertService : TWIMC,
			ready: false,
		};
	}

	setOptions() {
		if ( this.props.isLoading ) {
			this.loader = setTimeout( () => {
				this.setOptions();
			}, 1000 );
		} else {
			if ( this.props.proxyAssets && this.props.proxyAssets.length > 0 ) {
				const opts: Option[] = [];
				opts.push( {
					identity: -1,
					label: LVStrings.my_self,
				} );
				for ( let iCompany of this.props.proxyAssets ) {
					opts.push( {
						identity: iCompany.id,
						label: iCompany.name,
					} );
				}
				this.setState( {
					options: opts,
					requestForId: "-1",
				} );
			}
			this.setState( { ready: true } );
		}
	}

	componentWillUnmount() {
		clearTimeout( this.loader );
	}

	componentDidMount() {
		if ( this.props.isPropertyLevel ) {
			this.setState( { ready: true } );
		} else {
			this.setOptions();
		}
	}

	onOptionChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
		this.setState( { requestForId: e.target.value } );
	};

	render() {
		const property = this.context.property;
		if ( this.props.isPropertyLevel && property == null ) {
			return <Redirect to={ "/mydld" }/>;
		}
		const { service, ready, options, requestForId } = this.state;
		const title = LocaleUtil.getLocalValue( service.Name );
		const description = LocaleUtil.getLocalValue( service.Description );
		let headerTitle = title;
		if ( this.props.isPropertyLevel && property ) {
			const { val, lbl } = extractPropInfo( property );
			headerTitle = `${ title } - ${ lbl } : ${ val }`;
		}
		return <PageContent hideQLinks={ true } title={ headerTitle }>
			<PageTitle title={ title } paths={ [ DashboardBCItem ] }/>
			<div>
				{ description }
			</div>
			{ !ready && <div className="card green-list-feature p-3"><SpinnerLoader/></div> }
			{ ready && options && options.length > 0 && <>
				<div className="custom-control custom-radio custom-control-inline flex-column ">
					{ options.map( ( opt, ix ) => {
						const id = opt.identity + "";
						return <span key={ id }>
						<input
							id={ id }
							className="custom-control-input"
							type="radio" name={ opt.identity + "" } value={ opt.identity }
							onChange={ this.onOptionChange }
							checked={ requestForId + "" === opt.identity + "" }/>
							<label className="custom-control-label" htmlFor={ id }>{ LocaleUtil.getLocalValue( opt.label ) }</label>
					</span>;
					} ) }
				</div>
			</> }
			{ ready && <>
				<div className="form-group mt-3">
					<button
						className="btn btn_1 me-2" type="submit" id="btnContinue"
						onClick={ this.onContinue }>{ Strings.continue }</button>
					<a className="btn btn-grey text-decoration-none" onClick={ this.props.history.goBack }>{ Strings.cancel }</a>
				</div>
			</> }
		</PageContent>;
	}

	private onContinue = () => {
		if ( this.props.isPropertyLevel ) {
			this.submitTWICRequestForProperty();
		} else {
			const { requestForId } = this.state;
			let cmpId = requestForId === "-1" ? undefined : requestForId;
			showLoading();
			TabuRequestUtils.checkForExistingTWIC( getParticipantID()!, Strings as any, cmpId )
			.then( canProceed => {
				hideLoading();
				if ( canProceed ) {
					this.submitTWICRequest();
				} else {
					showErrorMessage( Strings.duplicate_twimc_error_msg );
				}
			} ).catch( reason => {
				hideLoading();
				showErrorMessage( reason.toString() );
			} );
		}
	};

	private submitTWICRequest() {
		showLoading();
		const { requestForId } = this.state;
		let cmpId = requestForId === "-1" ? undefined : requestForId ? requestForId + "" : undefined;
		const req: TWICRequest = {
			ParticipantId: "" + getParticipantID(),
			ProcedureId: TWIMC.ID,
			CompanyParticipantId: cmpId,
		};
		trackEventWithData( "TabuProcedure", "TrySubmit", req );
		saveTWIMC( req ).then( TabuUtils.validateResponse ).then( ( resp: TabuProcedure ) => {
			hideLoading();
			trackEventWithData( "TabuProcedure", "SubmitSuccess", {
				number: resp.ProcedureNumber,
				year: resp.ProcedureYear, ...req
			} );
			this.navigateToVoucher( this.props, resp );
		} ).catch( ( reason ) => {
			hideLoading();
			showErrorMessage( reason.toString() );
			trackEventWithData( "TabuProcedure", "SubmitFail", { reason: reason.toString(), ...req } );
		} );
	}

	private submitTWICRequestForProperty() {
		showLoading();
		const req: TWICRequest = {
			ParticipantId: "" + getParticipantID(),
			ProcedureId: PropertyDetailCertService.ID,
			PropertyId: ( this.context.property?.tabuPropertyId )!.toString(),
		};
		addAgentParticipantId( this.context.property!, req );
		trackEventWithData( "TabuProcedure", "TrySubmit", req );
		saveTWIMCForProperty( req ).then( TabuUtils.validateResponse ).then( ( resp: TabuProcedure ) => {
			hideLoading();
			trackEventWithData( "TabuProcedure", "SubmitSuccess", {
				number: resp.ProcedureNumber,
				year: resp.ProcedureYear, ...req
			} );
			this.navigateToVoucher( this.props, resp );
		} ).catch( ( reason ) => {
			hideLoading();
			showErrorMessage( reason.toString() );
			trackEventWithData( "TabuProcedure", "SubmitFail", { reason: reason.toString(), ...req } );
		} );
	}
}

export default portfolioConnector( TWIMCForm );
